'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { ApiClientError } from '@/shared/lib/api';
import { createLogger } from '@/shared/lib/logger';
import type { AssistantBlock } from '@/types/assistant';
import { assistantService } from '../api/assistant.service';

const logger = createLogger('Assistant');

/**
 * A safety stop for a recording left running. The server's daily allowance
 * is the real limit; this only keeps a forgotten tab from uploading minutes of
 * silence.
 */
const MAX_RECORDING_MS = 120_000;

export type VoiceState = 'idle' | 'recording' | 'transcribing';

export type VoiceFailure =
  /** The browser refused the microphone; no server text exists for that. */
  | { kind: 'permission' }
  /** The API refused or failed: its own words, and refusal blocks if any. */
  | { kind: 'api'; message: string; blocks: AssistantBlock[] }
  | { kind: 'network' };

/** Container types MediaRecorder can make, in the order Scribe prefers them. */
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

/**
 * Voice input (ADR-001 §8): record the whole utterance with MediaRecorder,
 * send it once, and hand the text to the input field for the user to edit
 * before sending. Nothing is sent to the conversation by itself.
 */
export function useVoiceInput(
  threadId: string | null,
  onText: (text: string) => void
) {
  const locale = useLocale();
  const [state, setState] = useState<VoiceState>('idle');
  const [failure, setFailure] = useState<VoiceFailure | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stopTimer = useRef<number | null>(null);

  const isSupported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;

  const transcribe = useCallback(
    async (audio: Blob) => {
      if (!threadId) return;
      setState('transcribing');
      try {
        const text = await assistantService.transcribe(threadId, audio, languages(locale));
        if (text.trim()) onText(text.trim());
      } catch (error) {
        if (error instanceof ApiClientError && error.status >= 400) {
          const meta = error.meta as { blocks?: AssistantBlock[] } | undefined;
          setFailure({ kind: 'api', message: error.message, blocks: meta?.blocks ?? [] });
        } else {
          setFailure({ kind: 'network' });
        }
      } finally {
        setState('idle');
      }
    },
    [locale, onText, threadId]
  );

  const stop = useCallback(() => {
    if (stopTimer.current !== null) window.clearTimeout(stopTimer.current);
    stopTimer.current = null;
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }, []);

  const start = useCallback(async () => {
    if (!isSupported || state !== 'idle') return;
    setFailure(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      logger.info('Microphone not available', { error: String(error) });
      setFailure({ kind: 'permission' });
      return;
    }
    const mimeType = MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    rec.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    rec.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      recorder.current = null;
      setStartedAt(null);
      const audio = new Blob(chunks, { type: rec.mimeType || mimeType || 'audio/webm' });
      if (audio.size > 0) void transcribe(audio);
      else setState('idle');
    };
    recorder.current = rec;
    rec.start();
    setState('recording');
    setStartedAt(Date.now());
    stopTimer.current = window.setTimeout(stop, MAX_RECORDING_MS);
  }, [isSupported, state, stop, transcribe]);

  // Leaving the panel mid-recording releases the microphone.
  useEffect(
    () => () => {
      if (stopTimer.current !== null) window.clearTimeout(stopTimer.current);
      const rec = recorder.current;
      if (rec) {
        rec.onstop = null;
        if (rec.state === 'recording') rec.stop();
        rec.stream.getTracks().forEach((track) => track.stop());
      }
    },
    []
  );

  return {
    isSupported,
    state,
    startedAt,
    failure,
    clearFailure: () => setFailure(null),
    start,
    stop,
  };
}

/** Languages to expect, most likely first: the app's, then the browser's. */
function languages(locale: string): string[] {
  const codes = [locale, ...(navigator.languages ?? [])].map((tag) =>
    tag.slice(0, 2).toLowerCase()
  );
  return Array.from(new Set(codes)).filter((code) => /^[a-z]{2}$/.test(code)).slice(0, 3);
}
